import { useCallback, useEffect, useState } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Logo } from "@/components/Logo";
import { DataTable, fmtDate } from "@/components/admin/DataTable";
import { useAuth } from "@/context/AuthContext";
import { api, euro } from "@/lib/api";

const TOOL_NAMES = { social_post: "Redes", marketing_ideas: "Ideas marketing", product_description: "Descripciones" };
const PAY_LABELS = { pending: "Pendiente", expired: "Caducado", failed: "Fallido", refunded: "Reembolsado" };

const COLUMNS = {
  quotes: [
    { key: "created_at", label: "Fecha", render: (r) => fmtDate(r.created_at) },
    { key: "name", label: "Nombre" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Teléfono" },
    { key: "business_type", label: "Negocio" },
    { key: "service", label: "Servicio" },
    { key: "message", label: "Mensaje", render: (r) => <span className="line-clamp-3">{r.message || "—"}</span> },
  ],
  leads: [
    { key: "created_at", label: "Fecha", render: (r) => fmtDate(r.created_at) },
    { key: "name", label: "Nombre" },
    { key: "email", label: "Email" },
    { key: "tool", label: "Herramienta", render: (r) => TOOL_NAMES[r.tool] || r.tool || "—" },
    { key: "uses", label: "Usos", render: (r) => r.uses },
  ],
  payments: [
    { key: "created_at", label: "Fecha", render: (r) => fmtDate(r.created_at) },
    { key: "package_name", label: "Paquete" },
    { key: "amount", label: "Importe", render: (r) => euro(r.amount) },
    { key: "customer_email", label: "Email" },
    { key: "payment_status", label: "Pago", render: (r) => (r.payment_status === "paid" ? <span className="font-semibold text-[#2A9D8F]">Pagado</span> : PAY_LABELS[r.payment_status] || r.payment_status) },
  ],
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-[#E8E1D7] bg-white p-6">
    <div className="text-xs font-semibold uppercase tracking-wider text-[#6E655F]">{label}</div>
    <div data-testid={testid} className="mt-2 font-display text-3xl font-bold text-[#1E1A17]">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [data, setData] = useState({ quotes: [], leads: [], payments: [] });
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    const [q, l, p, s] = await Promise.all(["quotes", "leads", "payments", "stats"].map((k) => api.get(`/admin/${k}`)));
    setData({ quotes: q.data, leads: l.data, payments: p.data });
    setStats(s.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onChange = (kind) => (id, field, status) => {
    setData((d) => ({ ...d, [kind]: d[kind].map((r) => (r.id === id ? { ...r, [field]: status } : r)) }));
    api.get("/admin/stats").then((s) => setStats(s.data));
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <header className="border-b border-[#E8E1D7] bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <div className="flex items-center gap-2">
            <span className="hidden text-sm text-[#6E655F] sm:inline">{user?.email}</span>
            <Button variant="ghost" size="icon" onClick={load} data-testid="admin-refresh-button"><RefreshCw className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={logout} data-testid="admin-logout-button" className="rounded-full"><LogOut className="mr-1.5 h-4 w-4" />Salir</Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-[#1E1A17] sm:text-4xl">Hola, equipo 👋</h1>
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Stat label="Solicitudes" value={stats?.quotes ?? "–"} testid="stat-quotes" />
          <Stat label="Contactos IA" value={stats?.leads ?? "–"} testid="stat-leads" />
          <Stat label="Usos de IA" value={stats?.generations ?? "–"} testid="stat-generations" />
          <Stat label="Clientas" value={stats?.clients ?? "–"} testid="stat-clients" />
          <Stat label="Ingresos" value={stats ? euro(stats.revenue) : "–"} testid="stat-revenue" />
        </div>
        <Tabs defaultValue="quotes" className="mt-10">
          <TabsList className="h-auto flex-wrap bg-[#E8E1D7]/60 p-1">
            <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Solicitudes ({data.quotes.length})</TabsTrigger>
            <TabsTrigger value="leads" data-testid="admin-tab-leads">Contactos IA ({data.leads.length})</TabsTrigger>
            <TabsTrigger value="payments" data-testid="admin-tab-payments">Pagos ({data.payments.length})</TabsTrigger>
          </TabsList>
          {["quotes", "leads", "payments"].map((k) => (
            <TabsContent key={k} value={k} className="mt-6">
              <DataTable kind={k} rows={data[k]} columns={COLUMNS[k]} statusField={k === "payments" ? "crm_status" : "status"} onChange={onChange(k)} />
            </TabsContent>
          ))}
        </Tabs>
      </main>
    </div>
  );
}
